import { clearLocalSession, getSessionRemovalError, isSessionTerminated, supabase } from "./supabaseClient";
import { AuthError, User, Session } from "@supabase/supabase-js";

export interface AuthResponse {
  user: User | null;
  session: Session | null;
  error?: AuthError | null;
}



export const googleSignIn = async (): Promise<AuthResponse> => {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      console.error("Error signing in with Google:", error);
      return { user: null, session: null, error };
    }

    // OAuth sign-in returns a URL for redirect, not user/session directly
    // The actual user/session will be available after the redirect
    return { user: null, session: null, error: null };
  } catch (error) {
    console.error("Unexpected error during Google sign-in:", error);
    return { user: null, session: null, error: error as AuthError };
  }
};

export const logout = async (): Promise<{ error?: AuthError | null }> => {
  const token = clearLocalSession();
  try {
    const { error } = token
      ? await supabase.auth.admin.signOut(token, "global")
      : { error: null };

    if (error) {
      console.error("Error during sign-out:", error);
      return { error };
    }

    return { error: getSessionRemovalError() };
  } catch (error) {
    console.error("Unexpected error during sign-out:", error);
    return { error: error as AuthError };
  }
};
export const getCurrentUser = async (): Promise<User | null> => {
  if (isSessionTerminated()) { return null; }
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return isSessionTerminated() ? null : user;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
};

export const getCurrentSession = async (): Promise<Session | null> => {
  if (isSessionTerminated()) { return null; }
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return isSessionTerminated() ? null : session;
  } catch (error) {
    console.error("Error getting current session:", error);
    return null;
  }
};

// Helper function to get access token for API calls
export const getAccessToken = async (): Promise<string | null> => {
  if (isSessionTerminated()) { return null; }
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return isSessionTerminated() ? null : session?.access_token || null;
  } catch (error) {
    console.error("Error getting access token:", error);
    return null;
  }
};
